import {faBook} from "@fortawesome/free-solid-svg-icons";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {boundMethod} from "autobind-decorator";
import {kebabCase} from "lodash";
import React from "react";
import {injectIntl, IntlShape} from "react-intl";

import {openURL2NewTab} from "@/services/download";
import http from "@/services/http";
import license from "@/services/license";
import {ILicenses} from "@/services/models";
import session from "@/services/session";
import {ANY_FALSE} from "@toolbox/functions/models";
import {DEFAULT_LANGUAGE} from "./models";

import {searchObjectsEvery} from "@toolbox/functions/object-iterator";
import {replaceLast} from "@toolbox/functions/string-operator";
import T, {intl2LocaleParts} from "@translate/T";

interface IUserManualProps {
    intl: IntlShape;
}

interface IUserManualState {
    licenses: ILicenses;
}

class UserManual extends React.PureComponent<
    IUserManualProps,
    IUserManualState
> {
    public readonly state: IUserManualState = {
        licenses: license.getDeviceLicenses(),
    };

    private unsubscribe?: () => void;

    private get api() {
        return "/resources/";
    }

    private get locale() {
        const {language} = intl2LocaleParts(this.props.intl.locale);
        if (language === DEFAULT_LANGUAGE.id) {
            return "";
        }

        return "-" + language;
    }

    private get localePdf() {
        const {language} = intl2LocaleParts(this.props.intl.locale);
        if (language === DEFAULT_LANGUAGE.id) {
            return "";
        }

        return "." + language;
    }

    private get token() {
        return `?token=${session.token ?? "not_found"}`;
    }

    public componentDidMount() {
        this.unsubscribe = license.subscribe({
            licenseChanged: () =>
                this.setState({licenses: license.getDeviceLicenses()}),
        });
    }

    public componentWillUnmount() {
        this.unsubscribe?.();
    }

    @boundMethod
    public async onClick(path: string) {
        try {
            const response = await http.get(`/api/resources`, {
                searchParams: {path},
            });
            if (!response.ok) {
                throw new http.HTTPError(response);
            }

            openURL2NewTab(path + this.token);
        } catch {
            const pathHtml = replaceLast(path, this.locale, "");
            const pathHtmlPdf = replaceLast(pathHtml, this.localePdf, "");

            openURL2NewTab(pathHtmlPdf + this.token);
        }
    }

    public render() {
        const {licenses} = this.state;

        if (searchObjectsEvery<ILicenses>(licenses, ANY_FALSE)) {
            return null;
        }

        return (
            <React.Fragment>
                {this.renderLumiFrac()}
                {this.renderLumiReader()}
                {this.renderLumiReaderXRay()}
                {this.renderLumiSizerFuge()}
                {this.renderLumiSpoc()}
                <div className="dropdown-divider" />
            </React.Fragment>
        );
    }

    private renderLumiFrac() {
        if (!this.state.licenses.frac) {
            return null;
        }

        return this.renderButton(
            <T>LUMiFrac manual</T>,
            `${this.api}Manual.LUMiFrac${this.localePdf}.pdf`,
        );
    }

    private renderLumiReader() {
        if (!this.state.licenses.reader) {
            return null;
        }

        return (
            <React.Fragment>
                {this.renderButton(
                    <T>LUMiReader analysis manual</T>,
                    `${this.api}manual-reader-analysis${this.locale}/index.html`,
                )}
                {this.renderButton(
                    <T>LUMiReader device manual</T>,
                    `${this.api}manual-reader-device${this.locale}/index.html`,
                )}
            </React.Fragment>
        );
    }

    private renderLumiReaderXRay() {
        if (!this.state.licenses.xray) {
            return null;
        }

        return (
            <React.Fragment>
                {this.renderButton(
                    <T>LUMiReader X-Ray analysis manual</T>,
                    `${this.api}manual-readerxray-analysis${this.locale}/index.html`,
                )}
                {this.renderButton(
                    <T>LUMiReader X-Ray device manual</T>,
                    `${this.api}manual-readerxray-device${this.locale}/index.html`,
                )}
            </React.Fragment>
        );
    }

    private renderLumiSizerFuge() {
        const {sizer, fuge} = this.state.licenses;
        if (!sizer && !fuge) {
            return null;
        }

        return this.renderButton(
            <T>LUMiSizer/Fuge manual</T>,
            `${this.api}Manual.LUMiSizer.LUMiFuge${this.localePdf}.pdf`,
        );
    }

    private renderLumiSpoc() {
        if (!this.state.licenses.spoc) {
            return null;
        }

        return this.renderButton(
            <T>LUMiSpoc manual</T>,
            `${this.api}Manual.LUMiSpoc${this.localePdf}.pdf`,
        );
    }

    private renderButton(title: JSX.Element, path: string) {
        const onClick = (e: React.SyntheticEvent) => {
            e.preventDefault();

            this.onClick(path);
        };

        return (
            <button
                type="button"
                id={kebabCase(path)}
                className="dropdown-item"
                onClick={onClick}
            >
                <FontAwesomeIcon
                    fixedWidth={true}
                    icon={faBook}
                    className="mr-1"
                />
                {title}
            </button>
        );
    }
}

export default injectIntl(UserManual);
